import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { getDownloadURL, ref } from 'firebase/storage';
import { deleteField, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth, db, storage } from './../../../firebase.js';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useSecondHeader } from './../../../contexts/SecondHeaderContext.js';
import { useUnitDataCache } from './../../../contexts/UnitDataCacheContext.js';

import left_arrow from "./../../../assets/icons/left_arrow.svg";
import right_arrow from "./../../../assets/icons/right_arrow.svg";
import data_error_svg from "./../../../assets/icons/data_error.svg";
import add_button_icon from "./../../../assets/icons/add-button.svg";

import { useTranslation } from "react-i18next";
import { getDisplayDateFromDate } from "../../../common/dateTools.js";
import {
    buildSortingListVoteSuggestions,
} from "../../../common/dangerousWasteSortingList.js";
import {
    getDangerousWasteRecordEvalReason,
    getDangerousWasteRecordValue,
    getWasteReceiverRecordFields,
    normalizeDangerousWasteColumns,
} from "../../../common/dangerousWasteRecordColumns.js";
import { canUserSeeLlmEvalReason } from "../../../common/llmEvalReasonAccess.js";
import { useUser } from "../../../contexts/UserContext.js";
import {
    MAX_IMAGE_ZOOM,
    MIN_IMAGE_ZOOM,
    canDrawDetections,
    clampImageZoom,
    formatRecordWeight,
    getImageDetections,
    getInitialImageView,
} from "../../../common/dangerousWasteRecordDetections.js";
import DetectionOverlay from "./DetectionOverlay.jsx";

import { Box, Slider, Switch } from "@mui/material";
import "./DangerousWasteRecordDialog.css";

const INCORRECT_RESULT_VOTE_VALUE = "__incorrect_result__";
const MAX_USER_COMMENT_LENGTH = 250;
const MIN_CANNOT_EVALUATE_COMMENT_LENGTH = 10;
const USER_EVAL_REQUESTS_FIELD = "user_eval_requests";
const USER_EVAL_REQUESTER_TYPE_USER = "user";
const EVAL_REQUEST_OUTCOME_EVALUATED = "evaluated";
const EVAL_REQUEST_OUTCOME_CANNOT_EVALUATE = "cannot_evaluate";

const sanitizeFirestoreMapKey = (value) => String(value || "")
    .replace(/[.#$/\[\]]/g, "_")
    .trim() || "entry";

const normalizeEvalRequestEntity = (value) => {
    if (value && typeof value === 'object') {
        const id = value.id != null ? String(value.id) : '';
        const type = value.type != null ? String(value.type) : USER_EVAL_REQUESTER_TYPE_USER;
        const label = value.label != null ? String(value.label) : null;
        return {
            id,
            type,
            ...(label ? { label } : {}),
        };
    }
    if (value != null && value !== '') {
        return {
            id: String(value),
            type: USER_EVAL_REQUESTER_TYPE_USER,
        };
    }
    return {
        id: '',
        type: USER_EVAL_REQUESTER_TYPE_USER,
    };
};

const normalizeEvalRequestsMap = (rawValue) => {
    if (!rawValue) return {};

    if (Array.isArray(rawValue)) {
        const converted = {};
        rawValue.forEach((request, index) => {
            const timestamp = Number(request?.timestamp || 0);
            const requester = normalizeEvalRequestEntity(request?.requester || request?.requested_by);
            const fallbackId = `${sanitizeFirestoreMapKey(requester.id || requester.type)}_${timestamp || index}_${index}`;
            const id = sanitizeFirestoreMapKey(request?.id || fallbackId);
            converted[id] = {
                id,
                timestamp,
                requester,
                resolved: Boolean(request?.resolved),
                resolved_timestamp: Number(request?.resolved_timestamp || 0) || null,
                resolved_by: request?.resolved_by ? normalizeEvalRequestEntity(request.resolved_by) : null,
                resolved_outcome: typeof request?.resolved_outcome === 'string'
                    ? request.resolved_outcome
                    : null,
                resolved_comment: typeof request?.resolved_comment === 'string'
                    ? request.resolved_comment
                    : null,
                resolved_comment_by: request?.resolved_comment_by
                    ? normalizeEvalRequestEntity(request.resolved_comment_by)
                    : null,
            };
        });
        return converted;
    }

    if (typeof rawValue === 'object') {
        const converted = {};
        Object.entries(rawValue).forEach(([key, request]) => {
            const timestamp = Number(request?.timestamp || 0);
            const requester = normalizeEvalRequestEntity(request?.requester || request?.requested_by);
            const id = sanitizeFirestoreMapKey(key || request?.id || `${requester.id}_${timestamp}`);
            converted[id] = {
                id,
                timestamp,
                requester,
                resolved: Boolean(request?.resolved),
                resolved_timestamp: Number(request?.resolved_timestamp || 0) || null,
                resolved_by: request?.resolved_by ? normalizeEvalRequestEntity(request.resolved_by) : null,
                resolved_outcome: typeof request?.resolved_outcome === 'string'
                    ? request.resolved_outcome
                    : null,
                resolved_comment: typeof request?.resolved_comment === 'string'
                    ? request.resolved_comment
                    : null,
                resolved_comment_by: request?.resolved_comment_by
                    ? normalizeEvalRequestEntity(request.resolved_comment_by)
                    : null,
            };
        });
        return converted;
    }

    return {};
};

const DangerousWasteRecordDialog = ({
    isOpen,
    initialIndex,
    localRecords,
    onDialogChangedRecord,
    onClose,
    dangerousWasteColumns,
    sortingListColumns,
    wasteReceiver,
}) => {
    const { t } = useTranslation();
    const [user] = useAuthState(auth);
    const { userData } = useUser();
    const { currentUnit } = useSecondHeader();
    const { getOtherUserInfo } = useUnitDataCache();

    const [currentRecordX, setCurrentRecordX] = useState(null);
    const [currentRecordIndex, setCurrentRecordIndex] = useState(null);
    const currentRecordIndexRef = useRef(currentRecordIndex);
    const [imageUrls, setImageUrls] = useState([]);
    const [imageLabels, setImageLabels] = useState([]);
    const [loadingImages, setLoadingImages] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [brightness, setBrightness] = useState(100);
    const [zoomLevels, setZoomLevels] = useState({});
    const [imageRotations, setImageRotations] = useState({});
    const [panOffsets, setPanOffsets] = useState({});
    const [draggingIndex, setDraggingIndex] = useState(null);
    const [panState, setPanState] = useState(null);
    // Per image: the camera that took it and what the unit detected on it, or null.
    const [imageDetections, setImageDetections] = useState([]);
    const [showDetections, setShowDetections] = useState(true);
    const [cellSizes, setCellSizes] = useState({});
    const imageCellRefs = useRef([]);
    // Set when a record's images finish loading, so the first layout after it frames each item.
    const pendingInitialViewRef = useRef(false);

    // Voting state
    const [localUserEvals, setLocalUserEvals] = useState({});
    const [localUserVoteVerdicts, setLocalUserVoteVerdicts] = useState({});
    const [localUserVoteActivity, setLocalUserVoteActivity] = useState({});
    const [localUserComments, setLocalUserComments] = useState({});
    const [localUserEvalRequests, setLocalUserEvalRequests] = useState({});
    const [userInfo, setUserInfo] = useState({});
    const [sortingListVoteSuggestions, setSortingListVoteSuggestions] = useState([]);
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [pendingIncorrectVote, setPendingIncorrectVote] = useState(false);
    const [addInput, setAddInput] = useState("");
    const [commentDraft, setCommentDraft] = useState("");
    const [cannotEvaluateFeedback, setCannotEvaluateFeedback] = useState("");
    const addInputRef = useRef(null);
    const commentDraftDirtyRef = useRef(false);
    const dropdownRef = useRef(null);
    const suggestionsDropdownRef = useRef(null);
    const dialogRootRef = useRef(null);
    const [suggestionsDropdownPosition, setSuggestionsDropdownPosition] = useState(null);
    /** Only reset add-category UI when the selected row identity changes, not on refetch/new object ref. */
    const lastVoteUiRecordKeyRef = useRef(null);
    /** After optimistic vote removal, refetches may still carry old `user_evals` briefly — ignore our uid until server matches. */
    const pendingSelfVoteRemovalRef = useRef(false);
    /** Image index that E/R rotation hotkeys apply to (hover or last rotate-button target). */
    const rotationHotkeyTargetRef = useRef(0);

    const currentRecordXRef = useRef(currentRecordX);
    currentRecordXRef.current = currentRecordX;

    const recordTs = currentRecordX?.timestamp != null ? String(currentRecordX.timestamp) : "";
    const frontList = currentRecordX?.front_images || [];
    const backList = currentRecordX?.back_images || [];
    const storagePathsList = Array.isArray(currentRecordX?.storage_paths) ? currentRecordX.storage_paths : [];
    const legacyImageNamesList = [...frontList, ...backList];
    const imagePathsList = storagePathsList.length > 0
        ? storagePathsList
        : legacyImageNamesList.map((name) => `images/${name}`);
    // Primitives only — new object identities on cache refresh must not re-fetch URLs (flashing).
    const imageLoadKey =
        !currentRecordX
            ? null
            : imagePathsList.length === 0
              ? `__empty__${recordTs}`
              : `${recordTs}\u001e${frontList.length}\u001e${JSON.stringify(imagePathsList)}`;

    const allSortingListColumns = useMemo(
        () => normalizeDangerousWasteColumns(sortingListColumns),
        [sortingListColumns],
    );
    // Titles and column count are the waste receiver's, never the record's own eval-time
    // ones: a column shows the display_name the receiver has configured for it now, and a
    // record only contributes the columns it actually carries a value for. A positional eval
    // names no columns of its own, so it lines up by index against the receiver's full
    // column set, hidden columns included.
    const displayedEvalFields = useMemo(
        () => getWasteReceiverRecordFields(
            currentRecordX,
            dangerousWasteColumns,
            allSortingListColumns.length > 0 ? allSortingListColumns : dangerousWasteColumns,
        ),
        [currentRecordX, dangerousWasteColumns, allSortingListColumns],
    );
    const displayColumns = useMemo(
        () => displayedEvalFields.map(({ id, display_name }) => ({ id, display_name })),
        [displayedEvalFields],
    );
    const getRecordRef = useCallback((_unitId, _record) => {
        if (_record?.record_path) {
            return doc(db, _record.record_path);
        }

        return doc(
            db,
            'units',
            _unitId,
            'dangerous-waste-records',
            _record.record_id,
        );
    }, []);

    const userVoteVerdictField = (isAdmin) => (isAdmin ? 'admin_vote_verdicts' : 'user_vote_verdicts');
    const userVoteActivityField = (isAdmin) => (isAdmin ? 'admin_vote_activity_at' : 'user_vote_activity_at');

    const getRequesterForCurrentUser = useCallback(() => {
        const requesterId = String(userData?.user_id || user?.uid || '');
        return {
            id: requesterId,
            type: USER_EVAL_REQUESTER_TYPE_USER,
            ...(userData?.name ? { label: String(userData.name) } : {}),
        };
    }, [userData, user?.uid]);

    const buildEvalRequestResolvePatch = useCallback((rawRequestsMap, actorRequester, nowTs, options = {}) => {
        const resolvedOutcome = String(options?.outcome || EVAL_REQUEST_OUTCOME_EVALUATED);
        const resolvedComment = typeof options?.comment === 'string'
            ? options.comment.trim()
            : '';
        const resolvedCommentBy = options?.commentBy
            ? normalizeEvalRequestEntity(options.commentBy)
            : null;

        const requestsMap = normalizeEvalRequestsMap(rawRequestsMap);
        const actor = normalizeEvalRequestEntity(actorRequester);
        const actorId = String(actor.id || '');
        const actorType = String(actor.type || USER_EVAL_REQUESTER_TYPE_USER);

        let changed = false;
        const nextMap = { ...requestsMap };
        const updates = {};

        Object.entries(requestsMap).forEach(([requestId, request]) => {
            if (request?.resolved) return;

            const requestTs = Number(request?.timestamp || 0);
            if (!Number.isFinite(requestTs) || requestTs <= 0 || nowTs <= requestTs) return;

            const requester = normalizeEvalRequestEntity(request?.requester);
            const requesterId = String(requester.id || '');
            const requesterType = String(requester.type || USER_EVAL_REQUESTER_TYPE_USER);
            if (requesterId === actorId && requesterType === actorType) return;

            const resolvedBy = {
                id: actorId,
                type: actorType,
            };

            nextMap[requestId] = {
                ...request,
                resolved: true,
                resolved_timestamp: nowTs,
                resolved_by: resolvedBy,
                resolved_outcome: resolvedOutcome,
                resolved_comment: resolvedComment || null,
                resolved_comment_by: resolvedComment && resolvedCommentBy
                    ? resolvedCommentBy
                    : null,
            };

            updates[`${USER_EVAL_REQUESTS_FIELD}.${requestId}.resolved`] = true;
            updates[`${USER_EVAL_REQUESTS_FIELD}.${requestId}.resolved_timestamp`] = nowTs;
            updates[`${USER_EVAL_REQUESTS_FIELD}.${requestId}.resolved_by`] = resolvedBy;
            updates[`${USER_EVAL_REQUESTS_FIELD}.${requestId}.resolved_outcome`] = resolvedOutcome;
            updates[`${USER_EVAL_REQUESTS_FIELD}.${requestId}.resolved_comment`] = resolvedComment || null;
            updates[`${USER_EVAL_REQUESTS_FIELD}.${requestId}.resolved_comment_by`] = resolvedComment && resolvedCommentBy
                ? resolvedCommentBy
                : null;
            changed = true;
        });

        return { changed, nextMap, updates };
    }, []);

    async function setDangerousWasteCustomEval(_unitId, _record, category, verdict = null, verdictFieldOverride = null) {
        console.log("setDangerousWasteCustomEval")
        try {
            console.log("Creating doc refference")
            const recordRef = getRecordRef(_unitId, _record);
            console.log("Debug A")
            const field = userEvalField(userData.isAdmin);
            const verdictField = verdictFieldOverride || userVoteVerdictField(userData.isAdmin);
            const activityField = userVoteActivityField(userData.isAdmin);
            const userId = userData.user_id
            const nowTs = Date.now();
            const resolvePatch = buildEvalRequestResolvePatch(
                _record[USER_EVAL_REQUESTS_FIELD],
                getRequesterForCurrentUser(),
                nowTs,
            );
            console.log("`${field}.${userId}`", `${field}.${userId}`)
            console.log("category", category)
            await updateDoc(recordRef, {
                [`${field}.${userId}`]: category,
                [`${verdictField}.${userId}`]: verdict,
                [`${activityField}.${userId}`]: nowTs,
                ...(resolvePatch.changed ? resolvePatch.updates : {}),
            });
            var recordNewValue = {..._record}
            recordNewValue[field] = { ..._record[field], [userId]: category };
            recordNewValue[verdictField] = { ..._record[verdictField], [userId]: verdict };
            recordNewValue[activityField] = { ..._record[activityField], [userId]: nowTs };
            if (resolvePatch.changed) {
                recordNewValue[USER_EVAL_REQUESTS_FIELD] = resolvePatch.nextMap;
                setLocalUserEvalRequests(resolvePatch.nextMap);
            }
            onDialogChangedRecord(recordNewValue)
            return true;
        } catch (error) {
            console.error('Error setting dangerous-waste custom eval:', error);
            return false;
        }
    }

    async function removeDangerousWasteCustomEval(_unitId, _record) {
        try {
            const recordRef = getRecordRef(_unitId, _record);
            const field = userEvalField(userData.isAdmin);
            const verdictField = userVoteVerdictField(userData.isAdmin);
            const activityField = userVoteActivityField(userData.isAdmin);
            const userId = userData.user_id
            const nowTs = Date.now();
            const resolvePatch = buildEvalRequestResolvePatch(
                _record[USER_EVAL_REQUESTS_FIELD],
                getRequesterForCurrentUser(),
                nowTs,
            );
            await updateDoc(recordRef, {
                [`${field}.${userId}`]: null,
                [`${verdictField}.${userId}`]: null,
                [`${activityField}.${userId}`]: nowTs,
                ...(resolvePatch.changed ? resolvePatch.updates : {}),
            });
            if (_record[field]) {
                const fieldValueNewValue = { ..._record[field] };
                delete fieldValueNewValue[userId];
                let recordNewValue = {..._record}
                recordNewValue[field] = fieldValueNewValue
                if (_record[verdictField]) {
                    const verdictValueNewValue = { ..._record[verdictField] };
                    delete verdictValueNewValue[userId];
                    recordNewValue[verdictField] = verdictValueNewValue;
                }
                recordNewValue[activityField] = { ..._record[activityField], [userId]: nowTs };
                if (resolvePatch.changed) {
                    recordNewValue[USER_EVAL_REQUESTS_FIELD] = resolvePatch.nextMap;
                    setLocalUserEvalRequests(resolvePatch.nextMap);
                }
                onDialogChangedRecord(recordNewValue)
            }
            return true;
        } catch (error) {
            console.error('Error removing dangerous-waste custom eval:', error);
            return false;
        }
    }

    async function setDangerousWasteCustomComment(_unitId, _record, comment) {
        try {
            const recordRef = getRecordRef(_unitId, _record);
            const field = 'user_comments';
            const userId = userData.user_id;
            await updateDoc(recordRef, {
                [`${field}.${userId}`]: comment,
            });
            const recordNewValue = { ..._record };
            recordNewValue[field] = { ..._record[field], [userId]: comment };
            onDialogChangedRecord(recordNewValue);
            return true;
        } catch (error) {
            console.error('Error setting dangerous-waste comment:', error);
            return false;
        }
    }

    async function removeDangerousWasteCustomComment(_unitId, _record) {
        try {
            const recordRef = getRecordRef(_unitId, _record);
            const field = 'user_comments';
            const userId = userData.user_id;
            await updateDoc(recordRef, {
                [`${field}.${userId}`]: null,
            });
            if (_record[field]) {
                const fieldValueNewValue = { ..._record[field] };
                delete fieldValueNewValue[userId];
                const recordNewValue = { ..._record };
                recordNewValue[field] = fieldValueNewValue;
                onDialogChangedRecord(recordNewValue);
            }
            return true;
        } catch (error) {
            console.error('Error removing dangerous-waste comment:', error);
            return false;
        }
    }

    async function addDangerousWasteEvalRequest(_unitId, _record, requestId, requestEntry) {
        if (!user) return false;
        try {
            const recordRef = getRecordRef(_unitId, _record);
            const previous = normalizeEvalRequestsMap(_record[USER_EVAL_REQUESTS_FIELD]);
            const nextRequests = {
                ...previous,
                [requestId]: requestEntry,
            };

            await updateDoc(recordRef, {
                [`${USER_EVAL_REQUESTS_FIELD}.${requestId}`]: requestEntry,
            });

            const recordNewValue = { ..._record };
            recordNewValue[USER_EVAL_REQUESTS_FIELD] = nextRequests;
            onDialogChangedRecord(recordNewValue);
            return true;
        } catch (error) {
            console.error('Error adding dangerous-waste eval request:', error);
            return false;
        }
    }

    async function removeDangerousWasteEvalRequest(_unitId, _record, requestId) {
        if (!user) return false;
        try {
            const recordRef = getRecordRef(_unitId, _record);
            await updateDoc(recordRef, {
                [`${USER_EVAL_REQUESTS_FIELD}.${requestId}`]: deleteField(),
            });

            const previous = normalizeEvalRequestsMap(_record[USER_EVAL_REQUESTS_FIELD]);
            const nextRequests = { ...previous };
            delete nextRequests[requestId];
            const recordNewValue = { ..._record };
            recordNewValue[USER_EVAL_REQUESTS_FIELD] = nextRequests;
            onDialogChangedRecord(recordNewValue);
            return true;
        } catch (error) {
            console.error('Error removing dangerous-waste eval request:', error);
            return false;
        }
    }

    const userEvalField = (isAdmin) => (isAdmin ? 'admin_evals' : 'user_evals');

    useEffect(() => {
        if (!isOpen || !imageLoadKey) {
            return undefined;
        }

        if (imageLoadKey.startsWith("__empty__")) {
            setLoadFailed(true);
            setLoadingImages(false);
            setImageUrls([]);
            setImageLabels([]);
            setImageDetections([]);
            return undefined;
        }

        const parts = imageLoadKey.split("\u001e");
        const frontLen = parseInt(parts[1], 10);
        let names;
        try {
            names = JSON.parse(parts[2]);
        } catch {
            setLoadFailed(true);
            setLoadingImages(false);
            return undefined;
        }
        if (!Array.isArray(names) || names.length === 0) {
            setLoadFailed(true);
            setLoadingImages(false);
            return undefined;
        }
        // The key holds the record's own image list, so detections looked up from the record
        // here belong to exactly the images being loaded.
        const detections = getImageDetections(currentRecordXRef.current, names);
        const labels = names.map((_, i) => (
            detections[i]?.cameraLabel || (i < frontLen ? "Front" : "Back")
        ));

        let cancelled = false;
        setLoadingImages(true);
        setLoadFailed(false);

        (async () => {
            try {
                const urls = await Promise.all(
                    names.map(async (name) => {
                        const imgRef = ref(storage, name);
                        return getDownloadURL(imgRef);
                    })
                );
                if (!cancelled) {
                    setImageUrls(urls);
                    setImageLabels(labels);
                    setImageDetections(detections);
                    pendingInitialViewRef.current = true;
                    setZoomLevels({});
                    setImageRotations({});
                    setPanOffsets({});
                    setBrightness(100);
                    rotationHotkeyTargetRef.current = 0;
                    setLoadingImages(false);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("Error loading dangerous waste images:", error);
                    setLoadFailed(true);
                    setLoadingImages(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, imageLoadKey]);

    const goToNextRecord = useCallback(() => {
        if (currentRecordIndexRef.current != null && currentRecordIndexRef.current > 0) {
            setCurrentRecordIndex(currentRecordIndexRef.current - 1);
        }
    }, []);

    const goToPreviousRecord = useCallback(() => {
        if (currentRecordIndexRef.current != null && currentRecordIndexRef.current < localRecords.length - 1) {
            setCurrentRecordIndex(currentRecordIndexRef.current + 1);
        }
    }, [localRecords]);

    useEffect(() => {
        if (initialIndex != null && currentRecordIndex == null) {
            setCurrentRecordIndex(initialIndex);
        }
    }, [initialIndex]);

    useEffect(() => {
        currentRecordIndexRef.current = currentRecordIndex;
    }, [currentRecordIndex]);

    useEffect(() => {
        if (currentRecordIndex != null && localRecords && currentRecordIndex < localRecords.length) {
            setCurrentRecordX(localRecords[currentRecordIndex]);
        }
    }, [currentRecordIndex, localRecords]);

    useEffect(() => {
        if (!isOpen || !currentRecordX?.record_path) {
            return undefined;
        }

        const recordRef = doc(db, currentRecordX.record_path);

        const unsubscribe = onSnapshot(recordRef, (snapshot) => {
            if (!snapshot.exists()) {
                return;
            }

            const nextRecord = {
                ...snapshot.data(),
                record_id: currentRecordX.record_id || snapshot.id,
                record_path: snapshot.ref.path,
                unit_id: currentRecordX.unit_id,
                company: currentRecordX.company,
            };

            setCurrentRecordX(nextRecord);
            onDialogChangedRecord?.(nextRecord);
        }, (error) => {
            console.error('Error listening to dangerous-waste record changes:', error);
        });

        return unsubscribe;
    }, [currentRecordX?.record_path, currentRecordX?.record_id, currentRecordX?.unit_id, currentRecordX?.company, isOpen, onDialogChangedRecord]);

    const vizTime = () => {
        if (!currentRecordX || !currentRecordX.timestamp) return '';
        const date = new Date(currentRecordX.timestamp);
        return getDisplayDateFromDate(date, userData?.language);
    };

    const getGridClass = () => {
        const count = imageUrls.length;
        if (count <= 2) return "grid-2";
        if (count === 4) return "grid-4";
        return "grid-6";
    };

    const handleZoomChange = (index, value) => {
        const v = clampImageZoom(value);
        setZoomLevels(prev => ({ ...prev, [index]: v }));
        if (v <= 1) {
            setPanOffsets(prev => ({ ...prev, [index]: { x: 0, y: 0 } }));
        }
    };

    const normalizeWheelDeltaY = (e) => {
        let y = e.deltaY;
        if (e.deltaMode === 1) {
            y *= 16;
        } else if (e.deltaMode === 2) {
            y *= 100;
        }
        return y;
    };

    const handleImageWheel = useCallback((index, e) => {
        e.preventDefault();
        e.stopPropagation();
        const dy = normalizeWheelDeltaY(e);
        let nextZoom;
        setZoomLevels((prev) => {
            const z = prev[index] ?? 1;
            nextZoom = clampImageZoom(z * Math.exp(-dy * 0.0012));
            return { ...prev, [index]: nextZoom };
        });
        if (nextZoom <= 1) {
            setPanOffsets((po) => ({ ...po, [index]: { x: 0, y: 0 } }));
        }
    }, []);

    const adjustImageRotation = useCallback((index, deltaDeg) => {
        setImageRotations((prev) => {
            const cur = prev[index] ?? 0;
            const next = ((cur + deltaDeg) % 360 + 360) % 360;
            if (next === 0) {
                const rest = { ...prev };
                delete rest[index];
                return rest;
            }
            return { ...prev, [index]: next };
        });
    }, []);

    const keyDown = useCallback((event) => {
        if (event.target?.closest?.('input, textarea, [contenteditable="true"]')) {
            return;
        }
        if (!isOpen) {
            return;
        }

        if (event.key === 'ArrowRight') {
            goToNextRecord();
            return;
        }
        if (event.key === 'ArrowLeft') {
            goToPreviousRecord();
            return;
        }

        const hasImages = imageUrls.length > 0 && !loadingImages && !loadFailed;
        if (!hasImages) {
            return;
        }

        const k = event.key;
        if (k === 'q' || k === 'Q') {
            event.preventDefault();
            const n = imageUrls.length;
            const idx = Math.max(0, Math.min(rotationHotkeyTargetRef.current, n - 1));
            adjustImageRotation(idx, -90);
            return;
        }
        if (k === 'e' || k === 'E') {
            event.preventDefault();
            const n = imageUrls.length;
            const idx = Math.max(0, Math.min(rotationHotkeyTargetRef.current, n - 1));
            adjustImageRotation(idx, 90);
        }
    }, [isOpen, imageUrls.length, loadingImages, loadFailed, goToNextRecord, goToPreviousRecord, adjustImageRotation]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        window.addEventListener('keydown', keyDown);
        return () => window.removeEventListener('keydown', keyDown);
    }, [isOpen, keyDown]);

    const handleImageMouseDown = (index, e) => {
        const zoom = zoomLevels[index] || 1;
        if (zoom <= 1) return;
        if (e.target.closest('.image-zoom-overlay')) return;
        e.preventDefault();
        const pan = panOffsets[index] || { x: 0, y: 0 };
        setPanState({
            index,
            startX: e.clientX,
            startY: e.clientY,
            origX: pan.x,
            origY: pan.y,
            zoom,
            rotationRad: ((imageRotations[index] ?? 0) * Math.PI) / 180,
        });
    };

    const handleSliderMouseDown = (index, e) => {
        e.preventDefault();
        setDraggingIndex(index);
    };

    useEffect(() => {
        if (!panState) return;

        const handleMouseMove = (e) => {
            // The pan sits inside the stage's rotate and scale, so a drag on screen is undone
            // through both to keep the image under the pointer.
            const dx = e.clientX - panState.startX;
            const dy = e.clientY - panState.startY;
            const cos = Math.cos(panState.rotationRad);
            const sin = Math.sin(panState.rotationRad);
            setPanOffsets(prev => ({
                ...prev,
                [panState.index]: {
                    x: panState.origX + (dx * cos + dy * sin) / panState.zoom,
                    y: panState.origY + (-dx * sin + dy * cos) / panState.zoom,
                }
            }));
        };

        const handleMouseUp = () => setPanState(null);

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [panState]);

    useEffect(() => {
        if (draggingIndex === null) return;

        const handleMouseMove = (e) => {
            const track = document.querySelector(`.zoom-track-${draggingIndex}`);
            if (!track) return;
            const rect = track.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            const value = MIN_IMAGE_ZOOM + ratio * (MAX_IMAGE_ZOOM - MIN_IMAGE_ZOOM);
            handleZoomChange(draggingIndex, value);
        };

        const handleMouseUp = () => {
            setDraggingIndex(null);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [draggingIndex]);

    // Track each image cell's size: the boxes are drawn in image pixels and need it to keep
    // their lines and labels the same size on screen at any zoom.
    useLayoutEffect(() => {
        if (loadingImages || loadFailed || imageUrls.length === 0) return undefined;
        const measure = () => {
            const next = {};
            imageCellRefs.current.forEach((cell, i) => {
                if (cell) next[i] = { width: cell.clientWidth, height: cell.clientHeight };
            });
            setCellSizes(next);
            return next;
        };
        const sizes = measure();

        if (pendingInitialViewRef.current) {
            pendingInitialViewRef.current = false;
            const zooms = {};
            const pans = {};
            imageDetections.forEach((detections, i) => {
                const view = getInitialImageView(detections, sizes[i]?.width, sizes[i]?.height);
                if (view) {
                    zooms[i] = view.zoom;
                    pans[i] = view.pan;
                }
            });
            setZoomLevels(zooms);
            setPanOffsets(pans);
        }

        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        imageCellRefs.current.forEach((cell) => cell && observer.observe(cell));
        return () => observer.disconnect();
    }, [loadingImages, loadFailed, imageUrls, imageDetections]);

    const recordWeight = formatRecordWeight(currentRecordX?.weight_g);
    const hasDrawableDetections = imageDetections.some(canDrawDetections);

    // Sync vote rows from the loaded record when the selection (or admin flag) changes.
    useEffect(() => {
        if (!currentRecordX) {
            lastVoteUiRecordKeyRef.current = null;
            pendingSelfVoteRemovalRef.current = false;
            return;
        }
        const evals = {};
        if (currentRecordX.user_evals) Object.assign(evals, currentRecordX.user_evals);
        if (currentRecordX.admin_evals) Object.assign(evals, currentRecordX.admin_evals);
        Object.keys(evals).forEach((k) => {
            if (evals[k] == null || evals[k] === '') delete evals[k];
        });

        const verdicts = {};
        if (currentRecordX.user_vote_verdicts) Object.assign(verdicts, currentRecordX.user_vote_verdicts);
        if (currentRecordX.admin_vote_verdicts) Object.assign(verdicts, currentRecordX.admin_vote_verdicts);

        const voteActivity = {};
        if (currentRecordX.user_vote_activity_at) Object.assign(voteActivity, currentRecordX.user_vote_activity_at);
        if (currentRecordX.admin_vote_activity_at) Object.assign(voteActivity, currentRecordX.admin_vote_activity_at);

        const evalRequests = normalizeEvalRequestsMap(currentRecordX[USER_EVAL_REQUESTS_FIELD]);

        const comments = {};
        if (currentRecordX.user_comments) Object.assign(comments, currentRecordX.user_comments);
        Object.keys(comments).forEach((k) => {
            if (comments[k] == null || comments[k] === '') delete comments[k];
        });

        const recordKey = `${currentRecordX.record_id ?? ''}\u001e${currentRecordX.timestamp != null ? String(currentRecordX.timestamp) : ''}`;
        const switchedRecord = lastVoteUiRecordKeyRef.current !== recordKey;

        if (switchedRecord) {
            lastVoteUiRecordKeyRef.current = recordKey;
            pendingSelfVoteRemovalRef.current = false;
            setPendingIncorrectVote(false);
            setIsAddingCategory(false);
            setAddInput("");
            setLocalUserEvals(evals);
            setLocalUserVoteVerdicts(verdicts);
            setLocalUserVoteActivity(voteActivity);
            setLocalUserComments(comments);
            setLocalUserEvalRequests(evalRequests);
            setCommentDraft((user?.uid && comments[user.uid]) || '');
            commentDraftDirtyRef.current = false;
            return;
        }

        setLocalUserEvals((prev) => {
            const next = { ...evals };
            if (user?.uid) {
                if (pendingSelfVoteRemovalRef.current) {
                    delete next[user.uid];
                } else {
                    const prevMine = prev[user.uid];
                    const serverMissingMine =
                        !Object.prototype.hasOwnProperty.call(evals, user.uid) ||
                        evals[user.uid] === undefined ||
                        evals[user.uid] === null ||
                        evals[user.uid] === '';
                    if (prevMine != null && prevMine !== '' && serverMissingMine) {
                        next[user.uid] = prevMine;
                    }
                }
            }
            return next;
        });

        setLocalUserVoteVerdicts((prev) => {
            const next = { ...verdicts };
            if (user?.uid && pendingSelfVoteRemovalRef.current) {
                delete next[user.uid];
            } else if (user?.uid && Object.prototype.hasOwnProperty.call(prev, user.uid) && !Object.prototype.hasOwnProperty.call(verdicts, user.uid)) {
                next[user.uid] = prev[user.uid];
            }
            return next;
        });

        setLocalUserVoteActivity(voteActivity);
        setLocalUserEvalRequests(evalRequests);

        setLocalUserComments((prev) => {
            const next = { ...comments };
            if (user?.uid && Object.prototype.hasOwnProperty.call(prev, user.uid) && !Object.prototype.hasOwnProperty.call(comments, user.uid)) {
                next[user.uid] = prev[user.uid];
            }
            return next;
        });

        if (!commentDraftDirtyRef.current) {
            setCommentDraft((user?.uid && comments[user.uid]) || '');
        }

        if (pendingSelfVoteRemovalRef.current && user?.uid) {
            const mine = evals[user.uid];
            if (mine == null || mine === '') pendingSelfVoteRemovalRef.current = false;
        }
    }, [currentRecordX, userData?.isAdmin, user?.uid]);

    // Union of voter IDs from the loaded record and local row state (covers first paint before
    // localUserEvals sync, and optimistic votes that are not in the record object yet).
    const participantIdsToFetch = useMemo(() => {
        if (!currentRecordX) return [];
        const fromRecord = {};
        if (currentRecordX.user_evals) Object.assign(fromRecord, currentRecordX.user_evals);
        if (currentRecordX.admin_evals) Object.assign(fromRecord, currentRecordX.admin_evals);
        if (currentRecordX.user_comments) Object.assign(fromRecord, currentRecordX.user_comments);
        const requesterIds = Object.values(normalizeEvalRequestsMap(currentRecordX[USER_EVAL_REQUESTS_FIELD]))
            .map((request) => request?.requester?.id)
            .filter(Boolean);
        const merged = { ...fromRecord, ...localUserEvals };
        return [...new Set([...Object.keys(merged), ...requesterIds])];
    }, [currentRecordX, localUserEvals]);

    // Fetch voter profiles (waste_receiver, etc.) for everyone shown in the vote rows.
    useEffect(() => {
        if (!isOpen || !currentRecordX) return;
        if (participantIdsToFetch.length === 0) {
            setUserInfo({});
            return undefined;
        }
        let cancelled = false;
        getOtherUserInfo(participantIdsToFetch).then((result) => {
            if (cancelled || !Array.isArray(result)) return;
            const dict = {};
            result.forEach((u) => {
                if (u && u.id) dict[u.id] = u;
            });
            setUserInfo(dict);
        }).catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [participantIdsToFetch, isOpen, currentRecordX, getOtherUserInfo]);

    const getUnitId = useCallback(() => {
        if (!currentRecordX?.unit_id) {
            if (!currentUnit) return null;
            if (currentUnit.unit_ranges) {
                const current = currentUnit.unit_ranges.find((r) => r.current);
                return current?.unit_id || null;
            }
            return currentUnit.unit_id || null;
        }
        return currentRecordX.unit_id;
    }, [currentRecordX, currentUnit]);

    useEffect(() => {
        if (!isOpen || !currentRecordX || !wasteReceiver) {
            setSortingListVoteSuggestions([]);
            return undefined;
        }

        setSortingListVoteSuggestions(
            buildSortingListVoteSuggestions(
                wasteReceiver.sortingList?.sortingItems || [],
                allSortingListColumns,
            ),
        );
        return undefined;
    }, [isOpen, currentRecordX, wasteReceiver, allSortingListColumns]);

    const allVoteSuggestions = useMemo(
        () => sortingListVoteSuggestions,
        [sortingListVoteSuggestions],
    );
    const allVoteSuggestionValues = useMemo(
        () => new Set(allVoteSuggestions.map((suggestion) => suggestion.value)),
        [allVoteSuggestions],
    );

    const userDataForId = useCallback((id) => {
        // Prefer full profile from UserContext for the signed-in user so flags like
        // waste_receiver are not overwritten by the slimmer getUsersInfo payload.
        if (user && id === user.uid) {
            if (!userData) return userInfo[id] || null;
            return { ...(userInfo[id] || {}), ...userData };
        }
        if (userInfo[id]) return userInfo[id];
        return null;
    }, [userInfo, user, userData]);

    const getColorForUser = useCallback((userId) => {
        const data = userDataForId(userId);
        return data?.color || "#eb6e65";
    }, [userDataForId]);

    const isWasteReceiverUser = useCallback((u) => {
        if (!u) return false;
        const receiverIds = Array.isArray(u.waste_receivers)
            ? u.waste_receivers
            : Array.isArray(u.wasteReceivers)
              ? u.wasteReceivers
              : [];
        if (receiverIds.length > 0) return true;
        const v = u.waste_receiver !== undefined ? u.waste_receiver : u.wasteReceiver;
        return v === true || v === 'true' || v === 1 || v === '1';
    }, []);

    const currentUserId = String(userData?.user_id || user?.uid || '');
    const currentUserIdentity = useMemo(() => {
        if (!currentUserId) return null;
        return userDataForId(currentUserId) || userData || null;
    }, [currentUserId, userDataForId, userData]);
    const canUseCannotEvaluate = useMemo(() => {
        if (!currentUserIdentity) return false;
        return userData?.isAdmin === true || isWasteReceiverUser(currentUserIdentity);
    }, [currentUserIdentity, userData?.isAdmin, isWasteReceiverUser]);

    const getUserVoteIDs = useCallback((category) => {
        if (!localUserEvals) return [];
        const ids = Object.keys(localUserEvals).filter((key) => localUserEvals[key] === category);
        if (user) {
            const idx = ids.indexOf(user.uid);
            if (idx > -1) {
                ids.splice(idx, 1);
                ids.unshift(user.uid);
            }
        }
        return ids;
    }, [localUserEvals, user]);

    const voteCategory = useCallback(async (category, verdict = null, verdictFieldOverride = null) => {
        if (!user || !currentRecordX) return;
        const unitId = getUnitId();
        if (!unitId) return;
        const verdictField = verdictFieldOverride || userVoteVerdictField(userData.isAdmin);

        // Toggle off if already voted for this category
        if (localUserEvals[user.uid] === category) {
            pendingSelfVoteRemovalRef.current = true;
            const next = { ...localUserEvals };
            delete next[user.uid];
            setLocalUserEvals(next);
            setLocalUserVoteVerdicts((prev) => {
                const nextVerdicts = { ...prev };
                delete nextVerdicts[user.uid];
                return nextVerdicts;
            });
            const removed = await removeDangerousWasteCustomEval(
                unitId,
                currentRecordX
            );
            if (!removed) pendingSelfVoteRemovalRef.current = false;
        } else {
            pendingSelfVoteRemovalRef.current = false;
            setLocalUserEvals({ ...localUserEvals, [user.uid]: category });
            setLocalUserVoteVerdicts((prev) => ({ ...prev, [user.uid]: verdict }));
            await setDangerousWasteCustomEval(unitId, currentRecordX, category, verdict, verdictField);
        }
    }, [
        user,
        currentRecordX,
        localUserEvals,
        userData,
        getUnitId,
        removeDangerousWasteCustomEval,
        setDangerousWasteCustomEval,
    ]);

    const clearIncorrectVoteVerdict = useCallback(async () => {
        if (!user || !currentRecordX) return;
        const unitId = getUnitId();
        if (!unitId) return;
        const verdictField = userVoteVerdictField(userData.isAdmin);
        const activityField = userVoteActivityField(userData.isAdmin);
        const nowTs = Date.now();
        const resolvePatch = buildEvalRequestResolvePatch(
            localUserEvalRequests,
            getRequesterForCurrentUser(),
            nowTs,
        );
        setPendingIncorrectVote(false);
        setIsAddingCategory(false);
        setAddInput("");
        setLocalUserVoteVerdicts((prev) => ({ ...prev, [user.uid]: null }));
        setLocalUserVoteActivity((prev) => ({ ...prev, [user.uid]: nowTs }));
        if (resolvePatch.changed) {
            setLocalUserEvalRequests(resolvePatch.nextMap);
        }
        try {
            const recordRef = getRecordRef(unitId, currentRecordX);
            await updateDoc(recordRef, {
                [`${verdictField}.${user.uid}`]: null,
                [`${activityField}.${user.uid}`]: nowTs,
                ...(resolvePatch.changed ? resolvePatch.updates : {}),
            });

            if (resolvePatch.changed) {
                const recordNewValue = { ...currentRecordX };
                recordNewValue[USER_EVAL_REQUESTS_FIELD] = resolvePatch.nextMap;
                recordNewValue[activityField] = { ...currentRecordX[activityField], [user.uid]: nowTs };
                onDialogChangedRecord(recordNewValue);
            }
        } catch (error) {
            console.error('Error clearing dangerous-waste vote verdict:', error);
        }
    }, [
        user,
        currentRecordX,
        getUnitId,
        getRecordRef,
        userData?.isAdmin,
        localUserEvalRequests,
        getRequesterForCurrentUser,
        buildEvalRequestResolvePatch,
    ]);

    const requestUserEvaluation = useCallback(async () => {
        if (!user || !currentRecordX) return;
        const unitId = getUnitId();
        if (!unitId) return;
        const requester = getRequesterForCurrentUser();
        const nowTs = Date.now();
        const requestId = sanitizeFirestoreMapKey(
            `${requester.type}_${requester.id || 'unknown'}_${nowTs}`,
        );
        const nextRequest = {
            id: requestId,
            requester,
            timestamp: nowTs,
            resolved: false,
            resolved_timestamp: null,
            resolved_by: null,
        };
        setLocalUserEvalRequests((prev) => ({ ...prev, [requestId]: nextRequest }));
        const saved = await addDangerousWasteEvalRequest(unitId, currentRecordX, requestId, nextRequest);
        if (!saved) {
            setLocalUserEvalRequests((prev) => {
                const next = { ...prev };
                delete next[requestId];
                return next;
            });
        }
    }, [user, currentRecordX, getUnitId, getRequesterForCurrentUser]);

    const removeUserEvaluationRequest = useCallback(async (requestId) => {
        if (!user || !currentRecordX || !requestId) return;
        const unitId = getUnitId();
        if (!unitId) return;

        const previous = normalizeEvalRequestsMap(localUserEvalRequests);
        const next = { ...previous };
        delete next[requestId];
        setLocalUserEvalRequests(next);

        const removed = await removeDangerousWasteEvalRequest(unitId, currentRecordX, requestId);
        if (!removed) {
            setLocalUserEvalRequests(previous);
        }
    }, [user, currentRecordX, getUnitId, localUserEvalRequests]);

    const submitNewCategory = useCallback((value) => {
        const trimmed = (value || "").trim();
        if (!trimmed) return;

        const lower = trimmed.toLowerCase();
        const suggestion =
            allVoteSuggestions.find((item) =>
                item.value.toLowerCase() === lower ||
                item.label.toLowerCase() === lower
            ) ||
            allVoteSuggestions.find((item) =>
                item.value.toLowerCase().includes(lower) ||
                item.label.toLowerCase().includes(lower)
            );

        if (!suggestion) return;

        const selectedValue = suggestion.value;
        const verdictField = userVoteVerdictField(userData.isAdmin);
        const verdict = pendingIncorrectVote ? 'incorrect' : null;
        voteCategory(selectedValue, verdict, verdictField);
        setPendingIncorrectVote(false);
        setAddInput("");
        setIsAddingCategory(false);
    }, [allVoteSuggestions, voteCategory, pendingIncorrectVote, userData?.isAdmin]);

    const filteredSuggestions = useMemo(() => {
        if (!addInput.trim()) return allVoteSuggestions;
        const lower = addInput.toLowerCase();
        return allVoteSuggestions.filter(
            (suggestion) =>
                suggestion.label.toLowerCase().includes(lower) ||
                suggestion.value.toLowerCase().includes(lower) ||
                suggestion.cells?.some((cell) =>
                    cell.label.toLowerCase().includes(lower) ||
                    cell.value.toLowerCase().includes(lower)
                ),
        );
    }, [addInput, allVoteSuggestions]);

    const renderVoteRow = useCallback((suggestion, wrapCellText = false) => (
        <div
            className="vote-row-cells"
            style={wrapCellText ? {
                gridTemplateColumns: `repeat(${suggestion.cells.length}, minmax(0, 1fr))`,
            } : undefined}
        >
            {suggestion.cells.map((cell) => (
                <div key={cell.id} className="vote-row-cell">
                    <span className="vote-row-cell-label">{cell.label}</span>
                    <span className="vote-row-cell-value">{cell.value || '\u2014'}</span>
                </div>
            ))}
        </div>
    ), []);

    // Close dropdown when clicking outside. Defer attaching the listener so the same
    // pointer gesture that opened the menu (click on "+") cannot be treated as outside.
    useEffect(() => {
        if (!isAddingCategory) return;

        const handleClickOutside = (e) => {
            const clickedAddControl = dropdownRef.current?.contains(e.target);
            const clickedSuggestions = suggestionsDropdownRef.current?.contains(e.target);
            if (!clickedAddControl && !clickedSuggestions) {
                setIsAddingCategory(false);
                setAddInput("");
            }
        };

        const timerId = window.setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 0);

        return () => {
            window.clearTimeout(timerId);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isAddingCategory]);

    useEffect(() => {
        if (!isAddingCategory) {
            setSuggestionsDropdownPosition(null);
            return undefined;
        }

        const updateDropdownPosition = () => {
            const input = addInputRef.current;
            if (!input) return;
            const rect = input.getBoundingClientRect();
            const dialogRect = dialogRootRef.current
                ?.querySelector('.dialog')
                ?.getBoundingClientRect();
            const left = Math.max(16, dialogRect ? dialogRect.left + 18 : rect.left);
            const availableWidth = Math.max(280, window.innerWidth - left - 16);
            const dialogInnerWidth = dialogRect
                ? Math.max(280, dialogRect.width - 36)
                : availableWidth;
            const hasRoomBelow = window.innerHeight - rect.bottom >= 260;
            setSuggestionsDropdownPosition({
                top: hasRoomBelow ? rect.bottom + 4 : Math.max(16, rect.top - 244),
                left,
                width: Math.min(dialogInnerWidth, availableWidth),
            });
        };

        updateDropdownPosition();
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);
        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [isAddingCategory]);

    useEffect(() => {
        if (isAddingCategory && addInputRef.current) {
            addInputRef.current.focus();
        }
    }, [isAddingCategory]);

    // Gated separately from the eval values above: the reason is the model's own explanation,
    // not a prediction, and only authorized users may read it.
    const llmEvalReason = useMemo(() => (
        canUserSeeLlmEvalReason(userData)
            ? getDangerousWasteRecordEvalReason(currentRecordX)
            : ''
    ), [currentRecordX, userData]);

    // The machine evaluation and user choices use the same complete-row format.
    const voteColumns = allSortingListColumns.length > 0
        ? allSortingListColumns
        : displayColumns;
    const machineVoteSuggestion = useMemo(() => {
        if (!currentRecordX || voteColumns.length === 0) return null;
        const machineRow = Object.fromEntries(
            voteColumns.map((column, index) => [
                column.id,
                getDangerousWasteRecordValue(currentRecordX, column.id, index),
            ]),
        );
        return buildSortingListVoteSuggestions(
            [machineRow],
            voteColumns,
        )[0] || null;
    }, [currentRecordX, voteColumns]);
    const machineCategory = machineVoteSuggestion?.value || null;
    const voteOptions = useMemo(() => {
        const opts = [];
        // Only rows with current votes are displayed. Keep them constrained to
        // the current sorting list.
        Object.values(localUserEvals).forEach((c) => {
            const isCurrentOption =
                allVoteSuggestionValues.has(c) &&
                c !== machineCategory;
            if (c && isCurrentOption && !opts.includes(c)) opts.push(c);
        });
        const hasMachineVote = machineCategory
            && Object.values(localUserEvals).some((vote) => vote === machineCategory);
        if (hasMachineVote && !opts.includes(machineCategory)) {
            opts.push(machineCategory);
        }
        return opts;
    }, [machineCategory, localUserEvals, allVoteSuggestionValues]);

    const currentUserVote = user?.uid ? localUserEvals[user.uid] : null;
    const hasCurrentUserExplicitVerdict = user?.uid
        ? Object.prototype.hasOwnProperty.call(localUserVoteVerdicts, user.uid)
        : false;
    const currentUserVoteVerdict = hasCurrentUserExplicitVerdict
        ? localUserVoteVerdicts[user.uid]
        : (currentUserVote && machineCategory
            ? (currentUserVote === machineCategory ? 'correct' : 'incorrect')
            : null);

    const getVoteVerdictForUser = useCallback((uid) => {
        const hasExplicitVerdict = Object.prototype.hasOwnProperty.call(localUserVoteVerdicts, uid);
        if (hasExplicitVerdict) return localUserVoteVerdicts[uid];

        const category = localUserEvals[uid];
        if (!category) return null;
        if (!machineCategory) return null;
        return category === machineCategory ? 'correct' : 'incorrect';
    }, [localUserEvals, localUserVoteVerdicts, machineCategory]);

    const currentUserComment = user?.uid ? (localUserComments[user.uid] || '') : '';
    const hasCurrentUserComment = currentUserComment.trim().length > 0;

    const commentRows = useMemo(() => {
        return Object.keys(localUserComments)
            .filter((uid) => (localUserComments[uid] || '').trim().length > 0)
            .sort((a, b) => {
                if (a === user?.uid) return -1;
                if (b === user?.uid) return 1;
                const aName = userDataForId(a)?.name || a;
                const bName = userDataForId(b)?.name || b;
                return aName.localeCompare(bName);
            });
    }, [localUserComments, user?.uid, userDataForId]);

    const formatEvalRequestTimestamp = useCallback((timestamp) => {
        const n = Number(timestamp);
        if (!Number.isFinite(n) || n <= 0) {
            return t("Ukendt tidspunkt");
        }
        return new Intl.DateTimeFormat(userData?.language || 'da-DK', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        }).format(new Date(n));
    }, [userData?.language, t]);

    const evalRequestRows = useMemo(() => {
        const requests = Object.values(normalizeEvalRequestsMap(localUserEvalRequests));
        return [...requests]
            .sort((a, b) => Number(b?.timestamp || 0) - Number(a?.timestamp || 0))
            .map((request, index) => {
                const requester = normalizeEvalRequestEntity(request?.requester);
                const requestTs = Number(request?.timestamp || 0);
                return {
                    id: request?.id || `${requester.id || 'request'}_${requestTs}_${index}`,
                    requester,
                    timestamp: requestTs,
                    answered: Boolean(request?.resolved),
                    canDelete:
                        requester.type === USER_EVAL_REQUESTER_TYPE_USER &&
                        requester.id === String(userData?.user_id || user?.uid || ''),
                };
            });
    }, [localUserEvalRequests, userData?.user_id, user?.uid]);

    const hasPendingEvalRequests = useMemo(
        () => evalRequestRows.some((request) => !request.answered),
        [evalRequestRows],
    );

    const saveCurrentUserComment = useCallback(async () => {
        if (!user || !currentRecordX) return;
        const unitId = getUnitId();
        if (!unitId) return;
        const nextComment = commentDraft.slice(0, MAX_USER_COMMENT_LENGTH).trim();
        commentDraftDirtyRef.current = false;
        if (!nextComment) {
            setCommentDraft('');
            await removeDangerousWasteCustomComment(unitId, currentRecordX);
            return;
        }
        setCommentDraft(nextComment);
        await setDangerousWasteCustomComment(unitId, currentRecordX, nextComment);
    }, [user, currentRecordX, getUnitId, commentDraft]);

    const resolveAsCannotEvaluate = useCallback(async () => {
        if (!user || !currentRecordX) return;

        if (!canUseCannotEvaluate) {
            setCannotEvaluateFeedback(t("Kun affaldsmodtagerbrugere og admins kan vælge Kan ikke vurderes."));
            return;
        }

        const unitId = getUnitId();
        if (!unitId) return;

        const nextComment = commentDraft.slice(0, MAX_USER_COMMENT_LENGTH).trim();
        if (nextComment.length < MIN_CANNOT_EVALUATE_COMMENT_LENGTH) {
            setCannotEvaluateFeedback(t("Tilføj en kommentar på mindst {{min}} tegn før du markerer Kan ikke vurderes.", {
                min: MIN_CANNOT_EVALUATE_COMMENT_LENGTH,
            }));
            return;
        }

        const nowTs = Date.now();
        const resolvePatch = buildEvalRequestResolvePatch(
            localUserEvalRequests,
            getRequesterForCurrentUser(),
            nowTs,
            {
                outcome: EVAL_REQUEST_OUTCOME_CANNOT_EVALUATE,
                comment: nextComment,
                commentBy: getRequesterForCurrentUser(),
            },
        );

        if (!resolvePatch.changed) {
            setCannotEvaluateFeedback(t("Der er ingen uafklarede anmodninger at løse."));
            return;
        }

        try {
            const recordRef = getRecordRef(unitId, currentRecordX);
            await updateDoc(recordRef, {
                [`user_comments.${currentUserId}`]: nextComment,
                ...resolvePatch.updates,
            });

            const nextComments = {
                ...(currentRecordX.user_comments || {}),
                [currentUserId]: nextComment,
            };

            setCannotEvaluateFeedback("");
            setCommentDraft(nextComment);
            commentDraftDirtyRef.current = false;
            setLocalUserComments((prev) => ({
                ...prev,
                [currentUserId]: nextComment,
            }));
            setLocalUserEvalRequests(resolvePatch.nextMap);

            const recordNewValue = {
                ...currentRecordX,
                user_comments: nextComments,
                [USER_EVAL_REQUESTS_FIELD]: resolvePatch.nextMap,
            };
            onDialogChangedRecord(recordNewValue);
        } catch (error) {
            console.error('Error resolving as cannot evaluate:', error);
            setCannotEvaluateFeedback(t("Kunne ikke markere som Kan ikke vurderes."));
        }
    }, [
        user,
        currentRecordX,
        canUseCannotEvaluate,
        getUnitId,
        commentDraft,
        buildEvalRequestResolvePatch,
        localUserEvalRequests,
        getRequesterForCurrentUser,
        getRecordRef,
        currentUserId,
        onDialogChangedRecord,
        t,
    ]);

    const clearCurrentUserComment = useCallback(async () => {
        if (!user || !currentRecordX) return;
        const unitId = getUnitId();
        if (!unitId) return;
        commentDraftDirtyRef.current = false;
        setCommentDraft('');
        await removeDangerousWasteCustomComment(unitId, currentRecordX);
    }, [user, currentRecordX, getUnitId]);

    useEffect(() => {
        if (cannotEvaluateFeedback && commentDraft.trim().length >= MIN_CANNOT_EVALUATE_COMMENT_LENGTH) {
            setCannotEvaluateFeedback("");
        }
    }, [commentDraft, cannotEvaluateFeedback]);

    const isCorrectVoteSelected = useMemo(() => {
        return currentUserVoteVerdict === 'correct';
    }, [currentUserVoteVerdict]);

    const isIncorrectVoteSelected = useMemo(() => {
        return pendingIncorrectVote || currentUserVoteVerdict === 'incorrect';
    }, [pendingIncorrectVote, currentUserVoteVerdict]);

    const voteCorrectResult = useCallback(() => {
        if (!machineCategory) return;
        setPendingIncorrectVote(false);
        if (currentUserVote === machineCategory && currentUserVoteVerdict === 'correct') {
            clearIncorrectVoteVerdict();
            return;
        }
        voteCategory(machineCategory, 'correct', userVoteVerdictField(userData.isAdmin));
    }, [machineCategory, voteCategory, currentUserVote, currentUserVoteVerdict, clearIncorrectVoteVerdict, userData?.isAdmin]);

    const voteIncorrectResult = useCallback(() => {
        if (isIncorrectVoteSelected) {
            clearIncorrectVoteVerdict();
            return;
        }

        setPendingIncorrectVote(true);
        setIsAddingCategory(true);
        setAddInput("");
    }, [isIncorrectVoteSelected, clearIncorrectVoteVerdict]);

    const renderVoteSection = () => (
        <div className="vote-section">
            <div className="vote-top-row">
                <div className="vote-controls-column">
                    <div className="vote-legend">
                        {t("waste_receiver_vote_legend", { letter: t("waste_receiver_initial") })}
                    </div>
                    <div className="vote-header">{t("Brugerevaluering")}</div>
                    <div className="quick-vote-buttons">
                        <button
                            type="button"
                            className={`quick-vote-btn quick-vote-btn-correct ${isCorrectVoteSelected ? 'active' : ''}`}
                            title={t("Resultat er korrekt")}
                            aria-label={t("Resultat er korrekt")}
                            disabled={!machineCategory}
                            onClick={voteCorrectResult}
                        >
                            ✓
                        </button>
                        <button
                            type="button"
                            className={`quick-vote-btn quick-vote-btn-incorrect ${isIncorrectVoteSelected ? 'active' : ''}`}
                            title={t("Resultat er forkert")}
                            aria-label={t("Resultat er forkert")}
                            onClick={voteIncorrectResult}
                        >
                            ✕
                        </button>
                    </div>
                </div>
                <div className="comment-section">
                    <div className="comment-header">{t("Kommentar")}</div>
                    <textarea
                        className="comment-input"
                        value={commentDraft}
                        maxLength={MAX_USER_COMMENT_LENGTH}
                        placeholder={t("Skriv en kort kommentar")}
                        onChange={(e) => {
                            commentDraftDirtyRef.current = true;
                            setCommentDraft(e.target.value);
                        }}
                        onBlur={() => {
                            if (commentDraftDirtyRef.current) {
                                saveCurrentUserComment();
                            }
                        }}
                    />
                    <div className="comment-footer">
                        <div className="comment-counter">
                            {commentDraft.length}/{MAX_USER_COMMENT_LENGTH}
                        </div>
                        <div className="comment-actions">
                            <button
                                type="button"
                                className="comment-action-btn"
                                onClick={saveCurrentUserComment}
                            >
                                {t("Gem")}
                            </button>
                            <button
                                type="button"
                                className="comment-action-btn comment-action-btn-secondary"
                                onClick={clearCurrentUserComment}
                                disabled={!hasCurrentUserComment}
                            >
                                {t("Ryd")}
                            </button>
                        </div>
                    </div>
                </div>
                {commentRows.length > 0 && (
                    <div className="comment-list comment-list-side">
                        {commentRows.map((uid) => {
                            const u = userDataForId(uid);
                            if (!u) return null;
                            const comment = localUserComments[uid];
                            return (
                                <div key={uid} className="comment-row">
                                    <div
                                        className="comment-avatar"
                                        style={{ backgroundColor: getColorForUser(uid) }}
                                        title={u.name}
                                    >
                                        {u.name?.slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className="comment-body">
                                        <div className="comment-user-name">{u.name}</div>
                                        <div className="comment-text-content">{comment}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="vote-options">
                {voteOptions.map((category) => {
                    const voters = getUserVoteIDs(category);
                    const suggestion = allVoteSuggestions.find((item) => item.value === category) ||
                        (category === machineCategory ? machineVoteSuggestion : null);
                    const categoryLabel =
                        category === INCORRECT_RESULT_VOTE_VALUE
                            ? t("Resultat er forkert")
                            : category;
                    return (
                        <div key={category} className="vote-row">
                            <div
                                className="vote-button vote-button-user"
                                onClick={() => voteCategory(category)}
                                title={suggestion ? suggestion.label : categoryLabel}
                            >
                                {suggestion ? renderVoteRow(suggestion) : categoryLabel}
                            </div>
                            <div className="vote-avatars">
                                {voters.map((uid) => {
                                    const u = userDataForId(uid);
                                    if (!u) return null;
                                    const showReceiverBadge = isWasteReceiverUser(u);
                                    const verdict = getVoteVerdictForUser(uid);
                                    return (
                                        <div
                                            key={uid}
                                            className="vote-avatar-wrap"
                                            title={u.name}
                                        >
                                            {verdict === 'correct' && (
                                                <span
                                                    className="vote-avatar-verdict-badge vote-avatar-verdict-badge-correct"
                                                    title={t("Resultat er korrekt")}
                                                    aria-hidden
                                                >
                                                    ✓
                                                </span>
                                            )}
                                            {verdict === 'incorrect' && (
                                                <span
                                                    className="vote-avatar-verdict-badge vote-avatar-verdict-badge-incorrect"
                                                    title={t("Resultat er forkert")}
                                                    aria-hidden
                                                >
                                                    ✕
                                                </span>
                                            )}
                                            {showReceiverBadge && (
                                                <span
                                                    className="vote-avatar-waste-receiver-badge"
                                                    title={t("waste_receiver_badge_tooltip")}
                                                    aria-hidden
                                                >
                                                    {t("waste_receiver_initial")}
                                                </span>
                                            )}
                                            <div
                                                className={`vote-avatar ${uid === user?.uid ? 'vote-avatar-current' : ''}`}
                                                style={{ backgroundColor: getColorForUser(uid) }}
                                            >
                                                {u.name?.slice(0, 2).toUpperCase()}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                <div className="vote-add-wrapper" ref={dropdownRef}>
                    {isAddingCategory ? (
                        <div className="vote-add-input-container">
                            <input
                                ref={addInputRef}
                                className="vote-add-input"
                                type="text"
                                placeholder={t("Ny kategori...")}
                                value={addInput}
                                onChange={(e) => setAddInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        submitNewCategory(addInput);
                                    } else if (e.key === 'Escape') {
                                        setPendingIncorrectVote(false);
                                        setIsAddingCategory(false);
                                        setAddInput("");
                                    }
                                }}
                            />
                            {filteredSuggestions.length > 0 && suggestionsDropdownPosition && dialogRootRef.current && createPortal(
                                <div
                                    ref={suggestionsDropdownRef}
                                    className="vote-suggestions-dropdown vote-suggestions-dropdown-portal"
                                    style={suggestionsDropdownPosition}
                                >
                                    {filteredSuggestions.map((suggestion, suggestionIndex) => (
                                        <div
                                            key={suggestion.id || `${suggestion.value}-${suggestionIndex}`}
                                            className="vote-suggestion-item"
                                            title={suggestion.label}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                submitNewCategory(suggestion.value);
                                            }}
                                        >
                                            {renderVoteRow(suggestion, true)}
                                        </div>
                                    ))}
                                </div>,
                                dialogRootRef.current,
                            )}
                        </div>
                    ) : (
                        <div
                            className="vote-add-button"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => setIsAddingCategory(true)}
                        >
                            <img src={add_button_icon} alt="Add" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div ref={dialogRootRef} className="DangerousWasteRecordDialog">
            {(currentRecordX && currentUnit) ? (
                <div className="dialog">
                    <button
                        type="button"
                        className="dialog-close-btn"
                        aria-label={t("Luk dialog")}
                        title={t("Luk dialog")}
                        onClick={onClose}
                    >
                        ×
                    </button>
                    <div className="dialog-main">
                        <div className="images-wrapper">
                        <div className="image-header">{t("Billeder")}</div>

                        {!loadingImages && !loadFailed && (
                            <div className={`images-grid ${getGridClass()}`}>
                                {imageUrls.map((url, i) => {
                                    const zoom = zoomLevels[i] || 1;
                                    const pan = panOffsets[i] || { x: 0, y: 0 };
                                    const rotationDeg = imageRotations[i] ?? 0;
                                    const fillPercent = ((zoom - MIN_IMAGE_ZOOM) / (MAX_IMAGE_ZOOM - MIN_IMAGE_ZOOM)) * 100;
                                    const detections = imageDetections[i];
                                    const cellSize = cellSizes[i];
                                    const drawDetections = showDetections && canDrawDetections(detections) && cellSize;
                                    return (
                                        <div
                                            key={i}
                                            ref={(cell) => { imageCellRefs.current[i] = cell; }}
                                            className={`image-cell${zoom > 1 ? ' pannable' : ''}${panState?.index === i ? ' panning' : ''}`}
                                            onMouseEnter={() => { rotationHotkeyTargetRef.current = i; }}
                                            onMouseDown={(e) => handleImageMouseDown(i, e)}
                                            onWheel={(e) => handleImageWheel(i, e)}
                                        >
                                            <span className="image-source-label">
                                                {t(imageLabels[i])}
                                            </span>
                                            <div className="image-pan-wrapper">
                                                <div
                                                    className="image-stage"
                                                    style={{
                                                        transform: `rotate(${rotationDeg}deg) scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
                                                    }}
                                                >
                                                    <img
                                                        src={url}
                                                        alt={`${t(imageLabels[i])} ${i + 1}`}
                                                        style={{ filter: `brightness(${brightness}%)` }}
                                                        draggable={false}
                                                    />
                                                    {drawDetections && (
                                                        <DetectionOverlay
                                                            detections={detections}
                                                            screenPixelsPerImagePixel={
                                                                Math.min(cellSize.width / detections.width, cellSize.height / detections.height) * zoom
                                                            }
                                                            itemLabel={t("Genstand")}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="image-zoom-overlay">
                                                <div className="image-rotation-btns">
                                                    <button
                                                        type="button"
                                                        className="image-rotate-btn"
                                                        aria-label={t("Rotate image left")}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={() => {
                                                            rotationHotkeyTargetRef.current = i;
                                                            adjustImageRotation(i, -90);
                                                        }}
                                                    >
                                                        ↺
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="image-rotate-btn"
                                                        aria-label={t("Rotate image right")}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={() => {
                                                            rotationHotkeyTargetRef.current = i;
                                                            adjustImageRotation(i, 90);
                                                        }}
                                                    >
                                                        ↻
                                                    </button>
                                                </div>
                                                <span className="zoom-label">{zoom.toFixed(1)}x</span>
                                                <div
                                                    className={`zoom-slider-track zoom-track-${i}`}
                                                    onMouseDown={(e) => handleSliderMouseDown(i, e)}
                                                >
                                                    <div
                                                        className="zoom-slider-fill"
                                                        style={{ width: `${fillPercent}%` }}
                                                    />
                                                    <div
                                                        className="zoom-slider-thumb"
                                                        style={{ left: `${fillPercent}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {loadFailed && (
                            <div className="images-grid grid-2" style={{ placeItems: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gridColumn: '1 / -1' }}>
                                    <img src={data_error_svg} width="80" height="80" alt="Error loading" />
                                    <div className="no-img">{t("Billede kunne ikke indlæses")}</div>
                                </div>
                            </div>
                        )}

                        <div className="brightness-section">
                            <div className="brightness-label">{t("Lysstyrke")}</div>
                            <Box sx={{ width: 180 }}>
                                <Slider
                                    value={brightness}
                                    defaultValue={100}
                                    min={0}
                                    max={200}
                                    color="-"
                                    aria-label="Brightness"
                                    valueLabelDisplay="auto"
                                    onChange={(_, v) => setBrightness(v)}
                                />
                            </Box>
                            {hasDrawableDetections && (
                                <label className="detections-toggle">
                                    <Switch
                                        size="small"
                                        checked={showDetections}
                                        onChange={(e) => setShowDetections(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--primary-green-500, #00C375)' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--primary-green-500, #00C375)' },
                                        }}
                                    />
                                    <span className="brightness-label">{t("Vis detektioner")}</span>
                                </label>
                            )}
                        </div>

                        <div className="cycle-btns">
                            <div
                                className="cycle-btn"
                                onClick={goToPreviousRecord}
                                style={currentRecordIndex >= localRecords.length - 1 ? { opacity: 0.4, cursor: 'default' } : {}}
                            >
                                <img src={left_arrow} alt="Previous" />
                            </div>
                            <div className="image-label">{vizTime()}</div>
                            <div
                                className="cycle-btn"
                                onClick={goToNextRecord}
                                style={currentRecordIndex < 1 ? { opacity: 0.4, cursor: 'default' } : {}}
                            >
                                <img src={right_arrow} alt="Next" />
                            </div>
                        </div>
                        </div>

                        <div className={`info-wrapper ${hasPendingEvalRequests ? 'eval-request-active' : ''}`}>
                            <div className="evaluation-header">{t("Evaluering")}</div>

                            <div className="eval-section">
                                {displayedEvalFields.map((field) => (
                                    <div className="eval-field" key={field.id}>
                                        <div className="eval-field-label">{field.display_name}</div>
                                        <div className={`eval-text-content ${!field.value ? 'empty' : ''}`}>
                                            {field.value || t("Ingen")}
                                        </div>
                                    </div>
                                ))}
                                {recordWeight && (
                                    <div className="eval-weight-row">
                                        <span className="eval-weight-label">{t("Vægt")}</span>
                                        <span className="eval-weight-value">{recordWeight}</span>
                                    </div>
                                )}
                                {llmEvalReason && (
                                    <div className="eval-field eval-reason-field">
                                        <div className="eval-field-label">{t("LLM Reason")}</div>
                                        <div className="eval-text-content eval-reason-content">
                                            {llmEvalReason}
                                        </div>
                                    </div>
                                )}
                                <div className="eval-request-section">
                                    <div className="eval-request-actions-row">
                                        <button
                                            type="button"
                                            className="eval-request-btn"
                                            onClick={requestUserEvaluation}
                                        >
                                            {t("Anmod om brugerevaluering")}
                                        </button>
                                        {canUseCannotEvaluate && (
                                            <button
                                                type="button"
                                                className="eval-request-btn eval-request-btn-cannot-evaluate"
                                                onClick={resolveAsCannotEvaluate}
                                                disabled={!hasPendingEvalRequests}
                                            >
                                                {t("Kan ikke vurderes")}
                                            </button>
                                        )}
                                    </div>
                                    {canUseCannotEvaluate && (
                                        <div className="eval-request-guidance">
                                            {t("Kræver kommentar på mindst {{min}} tegn.", {
                                                min: MIN_CANNOT_EVALUATE_COMMENT_LENGTH,
                                            })}
                                        </div>
                                    )}
                                    {cannotEvaluateFeedback && (
                                        <div className="eval-request-error">{cannotEvaluateFeedback}</div>
                                    )}

                                    <div className="eval-request-list">
                                        {evalRequestRows.map((request) => {
                                            const requesterUser = request.requester?.type === USER_EVAL_REQUESTER_TYPE_USER
                                                ? userDataForId(request.requester?.id)
                                                : null;
                                            const requesterName =
                                                request.requester?.label ||
                                                requesterUser?.name ||
                                                request.requester?.id ||
                                                t("Ukendt bruger");
                                            return (
                                                <div key={request.id} className="eval-request-row">
                                                    <div className="eval-request-row-head">
                                                        <div className="eval-request-row-title">
                                                            {t("anmodning om brugerevaluering")}
                                                        </div>
                                                        <div className="eval-request-actions">
                                                            {request.canDelete && (
                                                                <button
                                                                    type="button"
                                                                    className="eval-request-remove-btn"
                                                                    title={t("Fjern anmodning")}
                                                                    aria-label={t("Fjern anmodning")}
                                                                    onClick={() => removeUserEvaluationRequest(request.id)}
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        <div className="eval-request-status-wrap">
                                                            {request.answered && (
                                                                <span className="eval-request-answered" title={t("Besvaret")}>✓</span>
                                                            )}
                                                            <span className={`eval-request-status-label ${request.answered ? 'answered' : 'pending'}`}>
                                                                {request.answered ? t("Besvaret") : t("Ikke besvaret")}
                                                            </span>
                                                        </div>
                                                        </div>
                                                    </div>
                                                    <div className="eval-request-row-meta">
                                                        {requesterName} • {formatEvalRequestTimestamp(request.timestamp)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {renderVoteSection()}
                </div>
            ) : (
                <div>{t("Unit not loaded.")}</div>
            )}
        </div>
    );
};

export default DangerousWasteRecordDialog;
